import { Fragment, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { EmptyState, MetaChip, PrivacyChip, Quote, SectionLoading } from "@codel/dova-ds";
import {
  useMarkReportViewed,
  useReport,
  type BalanceSection,
  type MessagesSection,
  type MomentSection,
  type PrivateSection,
  type ReportSection,
  type TrySection,
} from "../hooks/reportHooks";
import { formatReportWeekRange } from "./reportFormatting";
import { REPORT_DETAIL_COPY, WEEKDAY_LABELS, weekdayLabel } from "./reportsCopy";
import { COUPLE_EVENTS } from "../analytics/events";
import { track } from "../analytics/track";
import { BackButton } from "../components/BackButton";

// The letter FRAME's words live in `reportsCopy.ts`; its CONTENT arrives on
// `report.content` and this file only renders what it is handed.

function MessagesBlock({ section }: { section: MessagesSection }) {
  const max = Math.max(...section.spark, 1);
  const points = section.spark
    .map((value, index) => {
      const x = section.spark.length === 1 ? 0 : (index / (section.spark.length - 1)) * 100;
      const y = 42 - (value / max) * 36;
      return `${x},${y}`;
    })
    .join(" ");
  return (
    <section className="border-t border-[var(--border)] py-6">
      <h2 className="font-serif text-xl font-medium text-[var(--text-primary)]">
        {section.heading}
      </h2>
      <div className="mt-2 flex items-baseline gap-4">
        <span className="font-serif text-2xl leading-none text-[var(--text-primary)]">{section.count}</span>
        <span className="text-sm text-[var(--text-secondary)]">{section.delta}</span>
      </div>
      {section.spark.length > 0 && (
        <>
          <svg
            viewBox="0 0 100 48"
            role="img"
            aria-label={REPORT_DETAIL_COPY.sparkLabel}
            className="mt-4 h-12 w-full overflow-visible"
            preserveAspectRatio="none"
          >
            <polyline
              fill="none"
              stroke="var(--interactive)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              points={points}
            />
          </svg>
          <div
            className="mt-1 grid text-center text-xs font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]"
            style={{ gridTemplateColumns: `repeat(${section.spark.length}, minmax(0, 1fr))` }}
          >
            {section.spark.map((_, index) => (
              <span key={`${WEEKDAY_LABELS[index] ?? index}-${index}`}>
                {weekdayLabel(index)}
              </span>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function BalanceBlock({ section }: { section: BalanceSection }) {
  const negativePct = Math.max(0, 100 - section.positivePct);
  return (
    <section className="border-t border-[var(--border)] py-6">
      <h2 className="font-serif text-xl font-medium text-[var(--text-primary)]">
        {section.heading}
      </h2>
      <div className="mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="font-serif text-2xl leading-none text-[var(--text-primary)]">{section.ratio}</span>
        <span className="text-sm text-[var(--text-secondary)]">{section.caption}</span>
      </div>
      <div
        role="img"
        aria-label={REPORT_DETAIL_COPY.balance.ariaLabel(section.positivePct, negativePct)}
        className="mt-4 flex h-2.5 w-full overflow-hidden rounded-[var(--radius-pill)] bg-[var(--muted)]"
      >
        <span style={{ width: `${section.positivePct}%` }} className="h-full bg-[var(--success)]" />
        <span style={{ width: `${negativePct}%` }} className="h-full bg-[var(--warning)]" />
      </div>
      <div className="mt-2 flex justify-between text-xs font-semibold">
        <span className="text-[var(--success)]">{REPORT_DETAIL_COPY.balance.positive}</span>
        <span className="text-[var(--warning)]">{REPORT_DETAIL_COPY.balance.negative}</span>
      </div>
      {section.topics.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {section.topics.map((topic) => (
            <MetaChip
              key={`${topic.label}-${topic.tone}`}
              className={
                topic.tone === "neg"
                  ? "border-[var(--warning)] text-[var(--warning)]"
                  : "border-[var(--success)] text-[var(--success)]"
              }
            >
              <span>{topic.label}</span>
              <b className="font-semibold tabular-nums">{topic.count}</b>
            </MetaChip>
          ))}
        </div>
      )}
    </section>
  );
}

function MomentBlock({ section }: { section: MomentSection }) {
  return (
    <section className="border-t border-[var(--border)] py-6">
      <h2 className="font-serif text-xl font-medium text-[var(--text-primary)]">
        {section.heading}
      </h2>
      <div className="mt-4">
        <Quote role="success" text={section.quote} attribution={`${section.speaker} - ${section.cite}`} />
      </div>
      <p className="mt-4 text-sm leading-6 text-[var(--text-secondary)]">{section.caption}</p>
    </section>
  );
}

function TryBlock({ section }: { section: TrySection }) {
  return (
    <section className="border-t border-[var(--border)] py-6">
      <h2 className="font-serif text-xl font-medium text-[var(--text-primary)]">
        {section.heading}
      </h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div className="rounded-md bg-[var(--muted)] px-4 py-4">
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--text-secondary)]">
            {REPORT_DETAIL_COPY.try.instead}
          </p>
          <p className="mt-1 text-base leading-6 text-[var(--text-primary)]">{section.instead}</p>
        </div>
        <div className="rounded-md bg-[var(--info-bg)] px-4 py-4">
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--info)]">
            {REPORT_DETAIL_COPY.try.better}
          </p>
          <p className="mt-1 text-base leading-6 text-[var(--text-primary)]">{section.try}</p>
        </div>
      </div>
      <p className="mt-4 text-sm leading-6 text-[var(--text-secondary)]">{section.caption}</p>
    </section>
  );
}

function PrivateSectionBlock({ section }: { section: PrivateSection }) {
  return (
    <section className="border-t border-[var(--border)] py-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-serif text-xl font-medium text-[var(--text-primary)]">
          {section.heading}
        </h2>
        <PrivacyChip />
      </div>
      <div className="mt-4 space-y-4">
        {section.body.map((paragraph) => (
          <p key={paragraph} className="text-base leading-6 text-[var(--text-primary)]">
            {paragraph}
          </p>
        ))}
        {section.ps && (
          <p className="text-sm leading-6 text-[var(--text-secondary)]">
            {section.ps}
          </p>
        )}
      </div>
    </section>
  );
}

function isMessagesSection(section: ReportSection): section is MessagesSection {
  return section.type === "messages" && Array.isArray((section as MessagesSection).spark);
}

function isBalanceSection(section: ReportSection): section is BalanceSection {
  return section.type === "balance" && typeof (section as BalanceSection).positivePct === "number";
}

function isMomentSection(section: ReportSection): section is MomentSection {
  return section.type === "moment" && typeof (section as MomentSection).quote === "string";
}

function isTrySection(section: ReportSection): section is TrySection {
  return section.type === "try" && typeof (section as TrySection).instead === "string";
}

function ReportSectionBlock({ section }: { section: ReportSection }) {
  if (isMessagesSection(section)) {
    return <MessagesBlock section={section} />;
  }
  if (isBalanceSection(section)) {
    return <BalanceBlock section={section} />;
  }
  if (isMomentSection(section)) {
    return <MomentBlock section={section} />;
  }
  if (isTrySection(section)) {
    return <TryBlock section={section} />;
  }
  return null;
}

export function ReportDetailPage() {
  const { reportUuid = "" } = useParams();
  const navigate = useNavigate();
  const report = useReport(reportUuid);
  const markViewed = useMarkReportViewed(reportUuid);
  const markedReportUuidRef = useRef<string | null>(null);

  useEffect(() => {
    if (!report.data || markedReportUuidRef.current === report.data.uuid) return;
    markedReportUuidRef.current = report.data.uuid;
    track(COUPLE_EVENTS.reportViewed, {
      report_id: report.data.uuid,
      is_first_view: report.data.viewedAt == null,
    });
    markViewed.mutate();
  }, [markViewed, report.data]);

  if (report.isLoading) {
    return (
      <main className="px-6 py-12">
        <SectionLoading label={REPORT_DETAIL_COPY.loadingLabel} />
      </main>
    );
  }

  if (report.error || !report.data) {
    return (
      <main className="px-6 py-12">
        <div className="mb-6">
          <BackButton label={REPORT_DETAIL_COPY.backLabel} onClick={() => navigate("/me/reports")} />
        </div>
        <EmptyState
          title={REPORT_DETAIL_COPY.errorTitle}
          body={REPORT_DETAIL_COPY.errorBody}
        />
      </main>
    );
  }

  const reportData = report.data;
  const week = formatReportWeekRange(reportData.weekStart, reportData.weekEnd);
  const hasMomentSection = reportData.content.sections.some(isMomentSection);

  return (
    <main className="px-6 py-8 sm:py-12">
      <div className="mb-6">
        <BackButton label={REPORT_DETAIL_COPY.backLabel} onClick={() => navigate("/me/reports")} />
      </div>
      <article className="mx-auto max-w-2xl">
        <header className="pb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--text-secondary)]">
            {reportData.content.cover.eyebrow}
          </p>
          <h1 className="mt-2 font-serif text-2xl font-medium leading-tight text-[var(--text-primary)]">
            {reportData.title}
          </h1>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">{week}</p>
          <p className="mt-4 text-lg leading-7 text-[var(--text-primary)]">
            {reportData.content.cover.read}
          </p>
        </header>

        {reportData.content.sections.map((section, index) => (
          <Fragment key={`${section.type}-${index}`}>
            <ReportSectionBlock section={section} />
            {reportData.privateSection && isMomentSection(section) ? (
              <PrivateSectionBlock section={reportData.privateSection} />
            ) : null}
          </Fragment>
        ))}

        {reportData.privateSection && !hasMomentSection ? (
          <PrivateSectionBlock section={reportData.privateSection} />
        ) : null}

        <footer className="border-t border-[var(--border)] px-1 py-8 text-center">
          <p className="font-serif text-xl font-medium text-[var(--text-primary)]">
            {reportData.content.signoff.main}
          </p>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            {reportData.content.signoff.sub}
          </p>
        </footer>
      </article>
    </main>
  );
}
